
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { todaysFeaturedExercise, weeklyExercises } from '../../mocks/daily-content';

export default function FitnessPage() {
  // Defensive destructuring – if the mock data is missing any field we fall back to sensible defaults
  const {
    mainExercise = { name: '', sets: 0, reps: 0, rest: '', description: '', tips: [] },
    assistanceExercise = { name: '', sets: 0, reps: 0, rest: '', description: '', tips: [] },
    mealPlan = {},
    peptideCombination = { goal: '', stack: [], timing: [], benefits: '' },
    date = '',
  } = todaysFeaturedExercise || {};

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/6dba469c5b90ff0c85b552cd352f6858.jpg"
            alt="Professional gym training environment"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-block px-4 py-2 bg-electric-blue/20 border border-electric-blue/40 rounded-full text-sm font-semibold text-white mb-6">
              {date}
            </div>
            <h1 className="font-display font-bold text-5xl md:text-6xl text-white mb-6">
              Fitness Workouts <span className="text-electric-blue">&amp; Programs</span>
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
              Complete training programs with exercises, nutrition plans, and peptide recommendations updated daily
            </p>
            <Link
              to="/booking"
              className="px-8 py-3 bg-electric-blue text-white rounded-lg text-sm font-semibold hover:bg-blue-500 transition-all whitespace-nowrap cursor-pointer"
            >
              Book Training Consultation
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-baby-blue/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Exercises */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Main Exercise */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-electric-blue rounded-xl">
                  <i className="ri-fitness-line text-2xl text-white"></i>
                </div>
                <div>
                  <h2 className="font-display font-bold text-2xl text-navy">Main Exercise</h2>
                  <p className="text-sm text-gray-600">Primary movement for today</p>
                </div>
              </div>
              <h3 className="font-display font-bold text-3xl text-navy mb-4">{mainExercise.name}</h3>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-baby-blue/20 rounded-lg p-4 text-center">
                  <div className="font-bold text-2xl text-navy">{mainExercise.sets}</div>
                  <div className="text-xs text-gray-600">Sets</div>
                </div>
                <div className="bg-baby-blue/20 rounded-lg p-4 text-center">
                  <div className="font-bold text-2xl text-navy">{mainExercise.reps}</div>
                  <div className="text-xs text-gray-600">Reps</div>
                </div>
                <div className="bg-baby-blue/20 rounded-lg p-4 text-center">
                  <div className="font-bold text-2xl text-navy">{mainExercise.rest}</div>
                  <div className="text-xs text-gray-600">Rest</div>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-6 leading-relaxed">{mainExercise.description}</p>
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-navy mb-3">Key Tips:</h4>
                {mainExercise.tips.map((tip, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-base text-electric-blue"></i>
                    </div>
                    <span className="text-sm text-gray-600">{tip}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Assistance Exercise */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-navy rounded-xl">
                  <i className="ri-run-line text-2xl text-white"></i>
                </div>
                <div>
                  <h2 className="font-display font-bold text-2xl text-navy">Assistance Exercise</h2>
                  <p className="text-sm text-gray-600">Complementary movement</p>
                </div>
              </div>
              <h3 className="font-display font-bold text-3xl text-navy mb-4">{assistanceExercise.name}</h3>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-baby-blue/20 rounded-lg p-4 text-center">
                  <div className="font-bold text-2xl text-navy">{assistanceExercise.sets}</div>
                  <div className="text-xs text-gray-600">Sets</div>
                </div>
                <div className="bg-baby-blue/20 rounded-lg p-4 text-center">
                  <div className="font-bold text-2xl text-navy">{assistanceExercise.reps}</div>
                  <div className="text-xs text-gray-600">Reps</div>
                </div>
                <div className="bg-baby-blue/20 rounded-lg p-4 text-center">
                  <div className="font-bold text-2xl text-navy">{assistanceExercise.rest}</div>
                  <div className="text-xs text-gray-600">Rest</div>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-6 leading-relaxed">{assistanceExercise.description}</p>
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-navy mb-3">Key Tips:</h4>
                {assistanceExercise.tips.map((tip, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-base text-electric-blue"></i>
                    </div>
                    <span className="text-sm text-gray-600">{tip}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Meal Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 shadow-xl mb-12"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 flex items-center justify-center bg-electric-blue rounded-xl">
                <i className="ri-restaurant-line text-2xl text-white"></i>
              </div>
              <div>
                <h2 className="font-display font-bold text-2xl text-navy">Today&apos;s Meal Plan</h2>
                <p className="text-sm text-gray-600">Optimized nutrition for your training</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(mealPlan).map(([key, meal]) => (
                <div
                  key={key}
                  className="bg-gradient-to-br from-baby-blue/10 to-white p-6 rounded-xl border border-baby-blue/30"
                >
                  <h3 className="font-display font-bold text-lg text-navy mb-3 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </h3>
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    <div className="text-center">
                      <div className="text-sm font-bold text-navy">{meal.calories}</div>
                      <div className="text-xs text-gray-500">cal</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-navy">{meal.protein}</div>
                      <div className="text-xs text-gray-500">protein</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-navy">{meal.carbs}</div>
                      <div className="text-xs text-gray-500">carbs</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-navy">{meal.fats}</div>
                      <div className="text-xs text-gray-500">fats</div>
                    </div>
                  </div>
                  <ul className="space-y-1">
                    {meal.items.map((item, idx) => (
                      <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                        <span className="text-electric-blue mt-1">&bull;</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Peptide Stack */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-navy to-navy-dark text-white rounded-2xl p-8 shadow-xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-electric-blue rounded-xl">
                <i className="ri-flask-line text-2xl text-white"></i>
              </div>
              <div>
                <h2 className="font-display font-bold text-2xl">Recommended Peptide Stack</h2>
                <p className="text-sm text-white/70">Goal: {peptideCombination.goal}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {peptideCombination.stack.map((peptide, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
                >
                  <div className="font-semibold text-base mb-1">{peptide}</div>
                  <div className="text-xs text-white/70">
                    {peptideCombination.timing?.[index] ?? ''}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm text-white/80 leading-relaxed">{peptideCombination.benefits}</p>
          </motion.div>
        </div>
      </section>

      {/* Weekly Schedule */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display font-bold text-4xl text-navy mb-4">Weekly Schedule</h2>
            <p className="text-lg text-gray-600">Your complete training week at a glance</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {weeklyExercises.map((day, index) => (
              <motion.div
                key={day.day ?? index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-gradient-to-br from-baby-blue/20 to-white p-6 rounded-xl border border-baby-blue/30 hover:shadow-lg transition-all"
              >
                <div className="font-display font-bold text-lg text-navy mb-2">{day.day}</div>
                <div className="text-xs text-electric-blue font-semibold mb-3">{day.focus}</div>
                <div className="space-y-2">
                  <div className="text-sm text-gray-700">
                    <strong>Main:</strong> {day.main}
                  </div>
                  <div className="text-sm text-gray-700">
                    <strong>Assist:</strong> {day.assistance}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-baby-blue/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-navy to-navy-dark text-white rounded-2xl p-12 text-center"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-electric-blue rounded-full mx-auto mb-6">
              <i className="ri-calendar-check-line text-3xl text-white"></i>
            </div>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-4">
              Want a Custom Program?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Book a consultation for a personalized training program, nutrition plan, and peptide protocol
              tailored to your goals
            </p>
            <Link
              to="/booking"
              className="inline-block px-8 py-4 bg-electric-blue text-white rounded-lg text-base font-semibold hover:bg-blue-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              Book Training Consultation
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
